import { Component } from "react";
import UploadSVG from "../../../Assets/svg/UploadSVG.svg";
import {
  UploadAndIngestFile,
  fetchProcessingStatus,
} from "../../../Actions/KnowledgeBaseBuilder";
import { FetchAllDepartmentsAction } from "../../../Actions/AdminUser";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import Spinner from "../../Animations/Spinner/Spinner";
import DropdownField from "./DropdownField";
import Modal from "../../Common/Modal";
import UploadProgressTracker from "./UploadProgressTracker";

class FileUploader extends Component {
  static propTypes = {
    fileUploaded: PropTypes.bool,
    fileID: PropTypes.number,
    UploadAndIngestFile: PropTypes.func.isRequired,
    handleShowAndHideFileUploader: PropTypes.func.isRequired,
    FetchAllDepartmentsAction: PropTypes.func.isRequired,
    departments: PropTypes.array,
    navigate: PropTypes.func.isRequired,
  };

  state = {
    selectedFile: null,
    fileName: "",
    description: "",
    selectedDepartment: "",
    selectedSubDepartment: "",
    isUploading: false,
    uploadSuccess: false,
    error: null,
    departments: [],
    selectedChatbotOption: "internal Chatbot",
    showConfirmModal: false,
    isCustomerService: false,
    ingestProgress: null, // { status, stage, file_id, file_name, error }
  };

  previousBodyOverflow = "";
  _socket = null;

  getIsCustomerServiceChatbot = (option = this.state.selectedChatbotOption) => {
    return option.toLowerCase() === "customer service chatbot";
  };

  getNormalizedDepartments = (source = this.state.departments) => {
    if (Array.isArray(source)) return source;
    return source?.departments || [];
  };

  componentDidMount() {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    this.props
      .FetchAllDepartmentsAction()
      .then((result) => {
        const depts = result?.departments || result || [];
        this.setState({ departments: Array.isArray(depts) ? depts : [] });
      })
      .catch((error) => {
        this.setState({
          error:
            error?.message ||
            "Unable to fetch departments. Please refresh and try again.",
        });
      });

    // Reconnect progress tracker if a file is still being processed
    const userId = this.props.currentUser?.id;
    if (userId) {
      this.props.fetchProcessingStatus().then((data) => {
        const inProgress = data?.processing?.[0];
        if (inProgress) {
          this._openIngestSocket(userId);
          this.setState({
            ingestProgress: {
              status: "PROCESSING",
              stage: "ingesting",
              file_id: inProgress.file_id,
              file_name: inProgress.file_name,
            },
          });
        }
      });
    }
  }

  componentWillUnmount() {
    document.body.style.overflow = this.previousBodyOverflow;
    if (this._socket) {
      this._socket.close();
      this._socket = null;
    }
  }

  _openIngestSocket = (userId) => {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.host;
    const url = `${protocol}://${host}/ws/file-ingest/${userId}/`;
    const ws = new WebSocket(url);
    this._socket = ws;

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        this.setState({ ingestProgress: data });
        if (data.status === "COMPLETED") {
          ws.close();
          setTimeout(() => this.props.handleShowAndHideFileUploader(), 3000);
        }
        if (data.status === "FAILED") {
          ws.close();
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => ws.close();
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.departments !== this.props.departments &&
      this.props.departments
    ) {
      this.setState({ departments: this.props.departments });
    }
  }

  handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      this.setState({ selectedFile: file, fileName: file.name });
    }
  };

  handleInputChange = (event) => {
    this.setState({ [event.target.name]: event.target.value });
  };

  handleNavigateToCreateDepartment = () => {
    this.props.navigate("/admin-dashboard/create-department");
  };

  handleChatbotOptionChange = (option) => {
    this.setState({
      selectedChatbotOption: option,
      selectedDepartment: "",
      selectedSubDepartment: "",
      error: null,
    });
  };

  validateForm = () => {
    const {
      selectedFile,
      selectedDepartment,
      selectedSubDepartment,
      departments,
      selectedChatbotOption,
    } = this.state;

    if (!selectedFile) {
      return "Please select a file before uploading.";
    }

    const isCustomerServiceChatbot =
      this.getIsCustomerServiceChatbot(selectedChatbotOption);

    if (isCustomerServiceChatbot) return null;

    if (!selectedDepartment) {
      return "Please select a department.";
    }

    const selectedDept = this.getNormalizedDepartments(departments)?.find(
      (dept) => dept.id.toString() === selectedDepartment
    );
    const subDepartments = selectedDept?.sub_departments || [];

    if (!subDepartments.length) {
      return "Selected department has no sub-departments. Please create one first.";
    }

    if (!selectedSubDepartment) {
      return "Please select a sub-department.";
    }

    return null;
  };

  handleUpload = () => {
    const validationError = this.validateForm();
    if (validationError) {
      this.setState({ error: validationError });
      return;
    }

    this.setState({
      showConfirmModal: true,
      isCustomerService: this.getIsCustomerServiceChatbot(),
      error: null,
    });
  };

  proceedWithUpload = () => {
    const {
      selectedFile,
      fileName,
      description,
      selectedDepartment,
      selectedSubDepartment,
      departments,
      selectedChatbotOption,
    } = this.state;

    this.setState({ isUploading: true, error: null, showConfirmModal: false });

    const isCustomerServiceChatbot =
      this.getIsCustomerServiceChatbot(selectedChatbotOption);
    const selectedDept = departments?.find(
      (dept) => dept.id.toString() === selectedDepartment
    );
    const selectedSubDept = selectedDept?.sub_departments?.find(
      (subDept) => subDept.id.toString() === selectedSubDepartment
    );

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("file_name", fileName || selectedFile.name);
    formData.append("description", description);
    formData.append("department_name", selectedDept?.name || "");
    formData.append("department_description", selectedDept?.description || "");
    formData.append("subdepartment_name", selectedSubDept?.name || "");
    formData.append(
      "subdepartment_description",
      selectedSubDept?.description || ""
    );

    // Add is_customer_service flag based on the selected option
    formData.append("is_customer_service", isCustomerServiceChatbot);

    // Open WebSocket before the HTTP call so we don't miss the first event
    const userId = this.props.currentUser?.id;
    if (userId) this._openIngestSocket(userId);

    this.props
      .UploadAndIngestFile(formData)
      .then((data) => {
        // HTTP succeeded — file is uploaded, Celery task is running
        // Progress updates arrive via WebSocket; show tracker state
        this.setState({
          isUploading: false,
          ingestProgress: {
            status: "PROCESSING",
            stage: "downloading",
            file_id: data.file_id,
            file_name: data.file_name,
          },
        });
      })
      .catch((error) => {
        if (this._socket) { this._socket.close(); this._socket = null; }
        this.setState({
          isUploading: false,
          error: error?.message || "Upload failed. Please verify the file and retry.",
        });
      });
  };

  renderConfirmationModal = () => {
    const { isCustomerService } = this.state;

    return (
      <div className="absolute inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
          <h3 className="text-xl font-bold mb-4">Confirmation Required</h3>
          <p className="mb-6">
            {isCustomerService
              ? "Are you sure you want to upload this document to the customer-facing chatbot? This content will be publicly accessible to customers."
              : "Are you sure you want to upload this file?"}
          </p>
          <div className="flex justify-end gap-4">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              onClick={() => this.setState({ showConfirmModal: false })}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-primary-blue text-white rounded hover:bg-blue-700"
              onClick={this.proceedWithUpload}
            >
              Confirm Upload
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderUploadForm = () => {
    const { departments: rawDepartments, selectedChatbotOption } = this.state;
    const { fileName, selectedDepartment, selectedSubDepartment } =
      this.state;

    const departments = this.getNormalizedDepartments(rawDepartments);

    const isCustomerServiceChatbot =
      this.getIsCustomerServiceChatbot(selectedChatbotOption);

    // Get sub-departments for selected department
    const selectedDept = departments?.find(
      (dept) => dept.id.toString() === selectedDepartment
    );
    const subDepartments = selectedDept?.sub_departments || [];
    const selectedSubDept = subDepartments.find(
      (subDept) => subDept.id.toString() === selectedSubDepartment
    );

    return (
      <div className="flex flex-col gap-[1rem] w-full">
        <div className="flex flex-col gap-[0.5rem]">
          <label
            className="text-[#000000] text-[1rem] font-[600]"
            htmlFor="selectedChatbotOption"
          >
            Select Upload Destination
          </label>
          <DropdownField
            id="selectedChatbotOption"
            name="selectedChatbotOption"
            value={selectedChatbotOption}
            onChange={(e) => this.handleChatbotOptionChange(e.target.value)}
            options={[
              { value: "internal Chatbot", label: "Internal Chatbot" },
              {
                value: "customer service chatbot",
                label: "Customer Service Chatbot",
              },
            ]}
          />

          {!isCustomerServiceChatbot && (
            <>
              <label className="font-[600] text-[1rem]" htmlFor="fileName">
                File Name
              </label>
              <input
                id="fileName"
                type="text"
                name="fileName"
                value={fileName}
                readOnly
                className="w-full h-[2.5rem] px-[1rem] border-[1px] border-[#D9D9D9] rounded-[0.5rem] outline-none bg-gray-50 text-gray-500 cursor-not-allowed"
                placeholder="File name will appear here"
              />
            </>
          )}
        </div>

        {!isCustomerServiceChatbot && (
          <>
            <div className="flex flex-col gap-[1rem]">
              <div className="flex flex-col gap-[0.5rem]">
                <label
                  className="font-[600] text-[1rem]"
                  htmlFor="selectedDepartment"
                >
                  Department
                </label>
                <DropdownField
                  id="selectedDepartment"
                  name="selectedDepartment"
                  value={selectedDepartment}
                  onChange={(e) => {
                    this.handleInputChange(e);
                    this.setState({ selectedSubDepartment: "" });
                  }}
                  placeholder={
                    Array.isArray(departments) && departments.length > 0
                      ? "Select Department"
                      : "Loading departments..."
                  }
                  disabled={!Array.isArray(departments) || departments.length === 0}
                  options={(Array.isArray(departments) ? departments : []).map(
                    (dept) => ({
                      value: String(dept.id),
                      label: dept.name || "Unnamed Department",
                    })
                  )}
                />
                {selectedDepartment &&
                  selectedDept &&
                  !selectedDept.description && (
                    <div className="flex flex-col gap-[1rem] items-center p-4 bg-[#FEF3F2] rounded-lg border border-[#FEE4E2]">
                      <p className="text-[#B42318] text-center">
                        Selected department does not have a description. Please
                        add a description first.
                      </p>
                      <button
                        onClick={this.handleNavigateToCreateDepartment}
                        className="px-4 py-2 bg-primary-blue text-white rounded-md hover:bg-primary-blue-dark transition-colors"
                      >
                        Update Department
                      </button>
                    </div>
                  )}
              </div>

              {selectedDepartment && (
                <>
                  {subDepartments && subDepartments.length > 0 ? (
                    <div className="flex flex-col gap-[0.5rem]">
                      <div className="font-[600] text-[1rem]">
                        Sub-Department
                      </div>
                      <DropdownField
                        id="selectedSubDepartment"
                        name="selectedSubDepartment"
                        value={selectedSubDepartment}
                        onChange={(e) => {
                          this.handleInputChange(e);
                        }}
                        placeholder="Select Sub-Department"
                        options={subDepartments.map((subDept) => ({
                          value: String(subDept.id),
                          label: subDept.name || "Unnamed Sub-Department",
                        }))}
                      />
                      {selectedSubDepartment &&
                        selectedSubDept &&
                        !selectedSubDept.description && (
                          <div className="flex flex-col gap-[1rem] items-center p-4 bg-[#FEF3F2] rounded-lg border border-[#FEE4E2]">
                            <p className="text-[#B42318] text-center">
                              Selected sub-department does not have a
                              description. Please add a description first.
                            </p>
                            <button
                              onClick={this.handleNavigateToCreateDepartment}
                              className="px-4 py-2 bg-primary-blue text-white rounded-md hover:bg-primary-blue-dark transition-colors"
                            >
                              Update Sub-Department
                            </button>
                          </div>
                        )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-[1rem] items-center p-4 bg-[#FEF3F2] rounded-lg border border-[#FEE4E2]">
                      <p className="text-[#B42318] text-center">
                        No sub-departments available for this department. Please
                        create a sub-department first.
                      </p>
                      <button
                        onClick={this.handleNavigateToCreateDepartment}
                        className="px-4 py-2 bg-primary-blue text-white rounded-md hover:bg-primary-blue-dark transition-colors"
                      >
                        Create Sub-Department
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* File Upload Section */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col h-44 items-center justify-center text-sm gap-4 border border-primary-blue-light border-dashed bg-[#E7F1FF] rounded-lg">
            <img src={UploadSVG} alt="Upload" className="w-16 h-16" />
            <div className="flex flex-col gap-2 items-center">
              <p>
                Drag & drop your file here or{" "}
                <button
                  type="button"
                  className="border p-2 bg-gray-100"
                  onClick={() => this.fileInput.click()}
                >
                  choose file
                </button>
                <input
                  type="file"
                  accept=".pdf,.csv,.xls,.xlsx,.txt,.doc,.docx"
                  style={{ display: "none" }}
                  ref={(input) => (this.fileInput = input)}
                  onChange={this.handleFileChange}
                />
              </p>
              {this.state.selectedFile && (
                <p className="text-green-600">
                  Selected File: {this.state.selectedFile.name}
                </p>
              )}
              <p className="text-[#EC474A]">Maximum Size: 3MB</p>
            </div>
          </div>

          {/* Upload/Ingest Button */}
          <button
            className={`py-3 font-SFPro font-bold text-base text-white rounded-full ${
              !isCustomerServiceChatbot &&
              (!selectedDepartment ||
                !selectedSubDepartment ||
                (selectedDepartment && subDepartments.length === 0))
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary-blue"
            }`}
            onClick={this.handleUpload}
            disabled={
              !this.state.selectedFile ||
              (!isCustomerServiceChatbot &&
                (!selectedDepartment ||
                  !selectedSubDepartment ||
                  (selectedDepartment && subDepartments.length === 0)))
            }
          >
            Upload File
          </button>
        </div>
      </div>
    );
  };

  renderLoadingState = () => {
    return (
      <div className="flex flex-col items-center justify-center gap-8 py-12">
        <Spinner />
        <p className="text-xl font-semibold">Uploading your file...</p>
      </div>
    );
  };

  renderSuccessState = () => {
    return (
      <div className="flex flex-col items-center justify-center gap-8 py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Upload Successful!
          </h3>
          <p className="text-gray-600">
            Your file has been successfully uploaded.
          </p>
        </div>
      </div>
    );
  };

  render() {
    const { handleShowAndHideFileUploader } = this.props;
    const { isUploading, ingestProgress, error, showConfirmModal } = this.state;

    return (
      <Modal onClose={handleShowAndHideFileUploader}>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {isUploading
          ? this.renderLoadingState()
          : ingestProgress
          ? <UploadProgressTracker
              fileName={ingestProgress.file_name}
              status={ingestProgress.status}
              stage={ingestProgress.stage}
              error={ingestProgress.error}
            />
          : this.renderUploadForm()}

        {showConfirmModal && this.renderConfirmationModal()}
      </Modal>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    fileUploaded: state.KnowledgeBaseBuilder?.fileUploaded,
    fileID: state.KnowledgeBaseBuilder?.fileID,
    departments: state.adminUser?.departments || [],
    currentUser: state.authentication?.user,
  };
};

export default withRouterHooks(
  connect(mapStateToProps, {
    UploadAndIngestFile,
    FetchAllDepartmentsAction,
    fetchProcessingStatus,
  })(FileUploader)
);
